'use client';

/**
 * "Your table" , the whole table session as one bill.
 *
 * The gap this closes: a table session could be added to but never inspected.
 * Every round created its own order with its own isolated `/pay/[orderId]`, and
 * nothing joined them, so a table that ordered three times had three unlinked
 * payment pages, no combined figure, and no answer to "what do we owe?". Rounds
 * placed from someone else's phone were invisible entirely, because the only
 * order list a guest had was the one saved in their own browser.
 *
 * This is deliberately a section inside the existing orders sheet rather than a
 * new surface. The guest action bar was consolidated down to one bar for a
 * reason; "my orders on this device" and "everything on this table" belong in the
 * same place, one above the other.
 */

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Loader2, Receipt, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { formatMoney as money } from '@/lib/money';

type BillItem = {
  name: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  seatLabel: string | null;
};

type BillSeat = {
  seatLabel: string | null;
  items: BillItem[];
  subtotal: number;
  orderCount: number;
};

type BillOrder = {
  id: string;
  orderNumber: string | null;
  status: string;
  paymentStatus: string | null;
  paymentVerificationStatus: string | null;
  guestName: string | null;
  total: number;
  createdAt: string | null;
  seatLabel: string | null;
  items: BillItem[];
  outstanding: boolean;
};

type Bill = {
  session: { id: string; code: string | null; status: string; openedAt: string | null };
  table: { label: string | null; capacity: number | null };
  branch: { id: string; name: string | null };
  currency: string | null;
  orders: BillOrder[];
  seats: BillSeat[];
  /** True when at least one outstanding round could be settled online right now. */
  canPayOnline: boolean;
  totals: { orderCount: number; billTotal: number; outstandingTotal: number; outstandingCount: number };
};

/** When an order actually placed each line: which seat it belongs to, if any. */
function seatName(seatLabel: string | null): string | null {
  if (!seatLabel) return null;
  return seatLabel;
}

/** Where an order has got to, in words a guest can act on. */
function orderStateLabel(order: BillOrder): string {
  if (order.paymentVerificationStatus === 'pending_review') return 'Payment being checked';
  if (!order.outstanding) return 'Paid';
  if (order.status === 'served') return 'Served , unpaid';
  if (order.status === 'ready') return 'Ready , waiting to be served';
  if (order.status === 'preparing') return 'Being prepared';
  if (order.status === 'accepted') return 'Accepted';
  return 'Placed';
}

export function TableSessionBill({
  sessionToken,
  open,
  className,
  onSessionOrderIds,
}: {
  sessionToken: string;
  /** Only fetches while the sheet holding it is actually open. */
  open: boolean;
  className?: string;
  /**
   * Reports which orders this bill itemises, so the parent can drop them from
   * its own device-history list. Without it the round a guest just placed is
   * printed twice, once here and once below.
   */
  onSessionOrderIds?: (orderIds: string[]) => void;
}) {
  const [bill, setBill] = useState<Bill | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billRequested, setBillRequested] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/public/table-sessions/bill', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionToken }),
          cache: 'no-store',
          signal,
        });
        const payload = await response.json();
        if (!response.ok || !payload.ok) throw new Error(payload.error || 'Could not load your table.');
        setBill(payload.bill);
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Could not load your table.');
      } finally {
        setLoading(false);
      }
    },
    [sessionToken],
  );

  useEffect(() => {
    if (!open) return;
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [open, load]);

  // Guests can't subscribe to postgres_changes on service_orders , the RLS select
  // grant is staff/admin-only by design, and a client-supplied realtime filter
  // would hand an anonymous visitor any session's rows. So "live" here is a light
  // poll while the sheet stays open, plus a refetch when the tab regains focus.
  // 30s is far inside the bill route's rate window and cheap enough to miss the
  // sheet sitting open on a table all evening.
  useEffect(() => {
    if (!open) return;
    const interval = window.setInterval(() => {
      void load();
    }, 30_000);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [open, load]);

  useEffect(() => {
    if (!bill) return;
    onSessionOrderIds?.(bill.orders.map((order) => order.id));
  }, [bill, onSessionOrderIds]);

  const requestBill = useCallback(async () => {
    setRequesting(true);
    try {
      const response = await fetch('/api/public/service-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken, requestType: 'request_bill' }),
      });
      const payload = await response.json();
      // An already-open request is the same outcome from the guest's side: staff
      // have been told. Treating it as an error would invite a second tap.
      if (response.ok || String(payload?.error || '').includes('already open')) {
        setBillRequested(true);
      } else {
        setError(payload?.error || 'Could not reach the staff just now.');
      }
    } catch {
      setError('Could not reach the staff just now.');
    } finally {
      setRequesting(false);
    }
  }, [sessionToken]);

  if (loading && !bill) {
    return (
      <div className={cn('flex items-center gap-2 rounded-2xl border border-[var(--pub-border-subtle)]/60 bg-card px-4 py-4', className)} role="status">
        <Loader2 className="size-4 animate-spin text-[var(--color-text-secondary)]" aria-hidden="true" />
        <p className="text-xs text-[var(--color-text-secondary)]">Loading your table…</p>
      </div>
    );
  }

  // A session that can't be read is not worth an error block here , the guest's
  // own device history still renders below, which is the more useful thing.
  if (!bill) {
    return error ? (
      <div className={cn('rounded-2xl border border-[var(--pub-border-subtle)]/60 bg-card px-4 py-3', className)}>
        <p className="text-xs text-[var(--color-text-secondary)]">{error}</p>
      </div>
    ) : null;
  }

  if (bill.orders.length === 0) return null;

  const { totals } = bill;

  return (
    <section className={cn('overflow-hidden rounded-2xl border border-[var(--pub-border-subtle)]/60 bg-card', className)}>
      <header className="flex items-start justify-between gap-3 border-b border-[var(--pub-border-subtle)]/60 px-4 py-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-base font-semibold text-[var(--color-text-primary)]">
            <Users className="size-4 shrink-0 text-[var(--color-text-secondary)]" aria-hidden="true" />
            {bill.table.label ? `Table ${bill.table.label}` : 'Your table'}
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
            {totals.orderCount} {totals.orderCount === 1 ? 'round' : 'rounds'} on this table
            {bill.session.code ? ` · ${bill.session.code}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-2xs uppercase tracking-widest text-[var(--color-text-secondary)]">Total</p>
          <p className="text-base font-bold tabular-nums">{money(totals.billTotal, bill.currency)}</p>
        </div>
      </header>

      <ul className="divide-y divide-border/60">
        {bill.orders.map((order) => (
          <li key={order.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                  {order.orderNumber ? `Order ${order.orderNumber}` : 'Order'}
                  {order.guestName ? (
                    <span className="text-[var(--color-text-secondary)]"> · {order.guestName}</span>
                  ) : null}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold tabular-nums">{money(order.total, bill.currency)}</p>
                <p
                  className={cn(
                    'text-2xs',
                    order.outstanding ? 'text-[var(--color-text-secondary)]' : 'font-medium text-[var(--color-text-primary)]',
                  )}
                >
                  {orderStateLabel(order)}
                </p>
              </div>
            </div>
            {order.items.length > 0 ? (
              <ul className="mt-2 space-y-1 border-t border-[var(--pub-border-subtle)]/40 pt-2">
                {order.items.map((item, index) => (
                  <li
                    key={`${item.name}-${index}`}
                    className="flex items-baseline justify-between gap-3 text-xs"
                  >
                    <span className="min-w-0 truncate text-[var(--color-text-secondary)]">
                      {item.quantity}× {item.name}
                      {item.seatLabel ? (
                        <span className="text-[var(--color-text-secondary)]/70"> · {item.seatLabel}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 tabular-nums text-[var(--color-text-secondary)]">
                      {money(item.lineTotal, bill.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{orderStateLabel(order)}</p>
            )}
            {order.outstanding ? (
              bill.canPayOnline ? (
                <Button asChild size="sm" variant="outline" className="mt-2.5 h-8 w-full text-xs">
                  <Link href={`/pay/${order.id}`}>
                    <Receipt className="size-3.5" aria-hidden="true" />
                    Pay this round
                  </Link>
                </Button>
              ) : (
                // No online rail on this branch (cash_to_waiter/pay_at_cashier
                // only): a /pay link would land on a page whose only content is
                // "show your reference to a staff member". Ask the staff to come
                // settle instead , same request_bill channel the footer uses.
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2.5 h-8 w-full text-xs"
                  disabled={requesting || billRequested}
                  onClick={() => void requestBill()}
                >
                  {requesting ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Receipt className="size-3.5" aria-hidden="true" />
                  )}
                  {billRequested ? 'Staff have been told' : 'Ask staff to settle'}
                </Button>
              )
            ) : null}
          </li>
        ))}
      </ul>

      {bill.seats.length > 0 ? (
        <section className="border-t border-[var(--pub-border-subtle)]/60 px-4 py-3">
          <p className="text-2xs uppercase tracking-widest text-[var(--color-text-secondary)]">Split by person</p>
          <ul className="mt-1 divide-y divide-border/40">
            {bill.seats.map((seat) => (
              <li
                key={seat.seatLabel ?? '__unseated__'}
                className="flex items-baseline justify-between gap-3 py-1.5"
              >
                <span className="min-w-0 truncate text-sm text-[var(--color-text-primary)]">
                  {seat.seatLabel ?? 'No seat'}
                  <span className="ml-1.5 text-2xs text-[var(--color-text-secondary)]">
                    {seat.orderCount} {seat.orderCount === 1 ? 'round' : 'rounds'}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums">
                  {money(seat.subtotal, bill.currency)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-2xs text-[var(--color-text-secondary)]">
            Item subtotals before tax and service , the round totals above are what gets settled.
          </p>
        </section>
      ) : null}

      {totals.outstandingCount > 0 ? (
        <footer className="space-y-2 border-t border-[var(--pub-border-subtle)]/60 bg-[var(--color-bg-sunken)]/30 px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-xs text-[var(--color-text-secondary)]">Still to pay</p>
            <p className="text-base font-bold tabular-nums">{money(totals.outstandingTotal, bill.currency)}</p>
          </div>
          {/* Paying is per-order today (each has its own charge and reference), so
              this asks a human for the table's bill rather than pretending to
              settle everything at once. */}
          <Button
            size="sm"
            variant={billRequested ? 'outline' : 'default'}
            className="h-9 w-full text-xs"
            disabled={requesting || billRequested}
            onClick={() => void requestBill()}
          >
            {requesting ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : null}
            {billRequested ? 'Staff have been told' : 'Ask staff for the bill'}
          </Button>
        </footer>
      ) : (
        <footer className="border-t border-[var(--pub-border-subtle)]/60 bg-[var(--color-bg-sunken)]/30 px-4 py-2.5">
          <p className="text-xs text-[var(--color-text-secondary)]">Everything on this table is paid.</p>
        </footer>
      )}

      {error ? <p className="px-4 pb-3 text-xs text-[var(--color-text-secondary)]">{error}</p> : null}
    </section>
  );
}

export default TableSessionBill;
